import React from "react";
// Meter Type page

// Default
import { useState } from "react";
import Swal from "sweetalert2";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Custom
import Layout from "../../layout";
import CustomInput from "../../forms/input/Input";
import DatePickerInput from "../../forms/input/DatePickerInput";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomToast from "../../components/toast/CustomToast";
import ContainedButton from "../../forms/button/ContainedButton";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { getTypeSpecificFields } from "../../utils/meterFieldsConfig";
import CustomSearch from "../search/Search";
import { ToastState } from "../../types";

// Types
interface MeterRegisterData {
  id?: any;
  meterCommodityType: string | OptionType;
  headEndSystem: string | OptionType;
  meterRegisterType: string | OptionType;
  meteringTechnology: string | OptionType;
  communicationInterface: string | OptionType;
  dataLogging: boolean;
  voltageCurrentInputs: boolean;
  reverseFlowDetection: boolean;
  pulseOutput: boolean;
  temperatureHumiditySensors: boolean;
  dataPorts: boolean;
  validFrom: string | Date | null;
  [key: string]: any;
}

interface SearchValue {
  type: string;
  value: string;
}

interface OptionType {
  value: string;
  label: string;
}

interface FormField {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  options?: OptionType[];
}

interface ApiError {
  message?: string;
  response?: {
    status: number;
    data?: any;
  };
}

// 9a3948b0-42b3-11f0-a9a0-9fd706385992 (gas) || 5a567b20-42bb-11f0-a9a0-9fd706385992 (water) || 1394fec0-42b9-11f0-a9a0-9fd706385992 (electricity)

const MeterType: React.FC = () => {
  const [searchValue, setSearchValue] = useState<SearchValue>({
    type: "id",
    value: "",
  });
  const [meterRegisterData, setMeterRegisterData] = useState<MeterRegisterData>(
    {
      meterCommodityType: "",
      headEndSystem: "",
      meterRegisterType: "",
      meteringTechnology: "",
      communicationInterface: "",
      dataLogging: false,
      voltageCurrentInputs: false,
      reverseFlowDetection: false,
      pulseOutput: false,
      temperatureHumiditySensors: false,
      dataPorts: false,
      validFrom: "",
    }
  );
  const [names, setNames] = useState<string[]>([]);
  const [counter, setCounter] = useState(Math.random());
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const [dynamicTypeSpecificFields, setDynamicTypeSpecificFields] = useState<
    FormField[]
  >([]);

  const generateUniqueName = (): string => {
    const newName = `metertype${counter}`;
    setNames([...names, newName]);
    setCounter(Math.random());
    return newName;
  };

  const meterCommodityTypeOptions: OptionType[] = [
    { value: "Electricity", label: "Electricity" },
    { value: "Water", label: "Water" },
    { value: "Gas", label: "Gas" },
  ];

  const headEndSystemOptions: OptionType[] = [
    { value: "SENSUS", label: "SENSUS" },
    { value: "ITRON", label: "ITRON" },
  ];

  const meterRegisterTypeOptions: OptionType[] = [
    { value: "Analog", label: "Analog" },
    { value: "Digital", label: "Digital" },
  ];

  const meteringTechnologyOptions: OptionType[] = [
    { value: "Electric", label: "Electric" },
    { value: "Electro-Mechanical", label: "Electro-Mechanical" },
    { value: "Smart", label: "Smart" },
  ];

  const communicationInterfaceOptions: OptionType[] = [
    { value: "Wired", label: "Wired" },
    { value: "Wireless", label: "Wireless" },
    { value: "Cellular", label: "Cellular" },
    { value: "RF", label: "RF" },
  ];

  // Handle meter commodity type change to load type-specific fields
  const handleMeterCommodityTypeChange = (selectedMeterType: OptionType) => {
    // Update the meter commodity type
    setMeterRegisterData({
      ...meterRegisterData,
      meterCommodityType: selectedMeterType,
    });

    // Load type-specific fields
    if (selectedMeterType?.value) {
      const typeSpecificFields = getTypeSpecificFields(
        selectedMeterType.value.toLowerCase()
      );
      setDynamicTypeSpecificFields(typeSpecificFields);
    } else {
      setDynamicTypeSpecificFields([]);
    }
  };

  // Dynamic form field renderer
  const renderTypeSpecificField = (field: FormField) => {
    const value = meterRegisterData[field.name] || "";

    switch (field.type) {
      case "text":
        return (
          <CustomInput
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type="text"
            value={value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setMeterRegisterData({
                ...meterRegisterData,
                [field.name]: event.target.value,
              });
            }}
          />
        );

      case "number":
        return (
          <CustomInput
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type="number"
            value={value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setMeterRegisterData({
                ...meterRegisterData,
                [field.name]: event.target.value,
              });
            }}
          />
        );

      case "select":
        return (
          <CustomOutlinedSelect
            key={field.name}
            label={field.label}
            options={field.options || []}
            placeholder={field.placeholder}
            value={value}
            onChange={(newValue) => {
              setMeterRegisterData({
                ...meterRegisterData,
                [field.name]: newValue.value as string,
              });
            }}
          />
        );

      case "checkbox":
        return (
          <FormControlLabel
            key={field.name}
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMeterRegisterData({
                    ...meterRegisterData,
                    [field.name]: event.target.checked,
                  });
                }}
              />
            }
            label={field.label}
          />
        );

      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    try {
      // Helper function to extract value from OptionType or string
      const getValue = (field: string | OptionType) => {
        if (typeof field === "object" && field?.value) {
          return field.value;
        }
        return field;
      };

      const requestData: any = {
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        type: "meterRegisterType",
        label: "NY Building",
        additionalInfo: {
          meterCommodityType: getValue(meterRegisterData.meterCommodityType),
          headendSystem: getValue(meterRegisterData.headEndSystem),
          meterRegisterType: getValue(meterRegisterData.meterRegisterType),
          meteringTechnology: getValue(meterRegisterData.meteringTechnology),
          communicationInterface: getValue(
            meterRegisterData.communicationInterface
          ),
          validFrom: meterRegisterData.validFrom,
          dataLogging: meterRegisterData.dataLogging,
          voltageCurrentInputs: meterRegisterData.voltageCurrentInputs,
          reverseFlowDetection: meterRegisterData.reverseFlowDetection,
          pulseOutput: meterRegisterData.pulseOutput,
          temperatureHumiditySensors:
            meterRegisterData.temperatureHumiditySensors,
          dataPorts: meterRegisterData.dataPorts,
          // Add dynamic type-specific fields
          ...dynamicTypeSpecificFields.reduce((acc: any, field) => {
            const value = meterRegisterData[field.name];
            if (value !== undefined && value !== "") {
              if (
                field.type === "select" &&
                typeof value === "object" &&
                value?.value
              ) {
                acc[field.name] = value.value;
              } else {
                acc[field.name] = value;
              }
            }
            return acc;
          }, {}),
        },
      };

      if (meterRegisterData?.id && meterRegisterData?.id?.id) {
        requestData.id = {
          id: meterRegisterData.id?.id,
          entityType: "ASSET",
        };
      }

      const response = await fetchPOSTRequest(`asset`, requestData);

      setSearchValue({ ...searchValue, value: response?.id?.id });

      console.log(response, "------save response");

      if (response?.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.id?.id + "Update successful!",
        });
      } else {
        setToast({
          message: response?.message,
          severity: "error",
        });

        setMeterRegisterData({
          meterCommodityType: "",
          headEndSystem: "",
          meterRegisterType: "",
          meteringTechnology: "",
          communicationInterface: "",
          dataLogging: false,
          voltageCurrentInputs: false,
          reverseFlowDetection: false,
          pulseOutput: false,
          temperatureHumiditySensors: false,
          dataPorts: false,
          validFrom: "",
        });
      }
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Error during PUT request:", apiError.message);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error during Update request",
      });
    }
  };

  const handleSearch = async () => {
    try {
      if (!searchValue?.value?.trim()) {
        setToast({
          message: "Please enter a valid search value.",
          severity: "error",
        });
        return;
      }

      const response = await fetchGETRequest(`asset/${searchValue?.value}`);

      if (response?.additionalInfo) {
        const {
          meterCommodityType,
          headendSystem,
          meterRegisterType,
          meteringTechnology,
          communicationInterface,
          validFrom,
          dataLogging,
          voltageCurrentInputs,
          reverseFlowDetection,
          pulseOutput,
          temperatureHumiditySensors,
          dataPorts,
        } = response?.additionalInfo;

        const selectedMeterType = meterCommodityTypeOptions.find(
          (item) => item.value === meterCommodityType
        );

        // Load type-specific fields for the found meter type
        if (selectedMeterType?.value) {
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase()
          );
          setDynamicTypeSpecificFields(typeSpecificFields);
        }

        // Prepare meter register data with all fields
        const newMeterData: any = {
          id: response?.data?.id,
          meterCommodityType: selectedMeterType,
          headEndSystem: headEndSystemOptions.find(
            (item) => item.value === headendSystem
          ),
          meterRegisterType: meterRegisterTypeOptions.find(
            (item) => item.value === meterRegisterType
          ),
          meteringTechnology: meteringTechnologyOptions.find(
            (item) => item.value === meteringTechnology
          ),
          communicationInterface: communicationInterfaceOptions.find(
            (item) => item.value === communicationInterface
          ),
          dataLogging: dataLogging,
          voltageCurrentInputs: voltageCurrentInputs,
          reverseFlowDetection: reverseFlowDetection,
          pulseOutput: pulseOutput,
          temperatureHumiditySensors: temperatureHumiditySensors,
          dataPorts: dataPorts,
          validFrom: validFrom ? new Date(validFrom) : null,
        };

        // Add dynamic type-specific field values from API response
        if (selectedMeterType?.value) {
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase()
          );
          typeSpecificFields.forEach((field: any) => {
            if (response?.additionalInfo.hasOwnProperty(field.name)) {
              const apiValue = response.additionalInfo[field.name];
              if (field.type === "select" && field.options) {
                newMeterData[field.name] =
                  field.options.find(
                    (option: any) => option.value === apiValue
                  ) || null;
              } else if (field.type === "date" && apiValue) {
                newMeterData[field.name] = new Date(apiValue);
              } else {
                newMeterData[field.name] = apiValue;
              }
            }
          });
        }

        setMeterRegisterData(newMeterData);
      } else {
        setToast({ message: "Meter Type not found", severity: "error" });
      }
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Error fetching consumer:", apiError.message);
    }
  };

  return (
    <Layout>
      <CustomToast
        open={toast.message}
        severity={toast.severity}
        setToast={setToast}
      />

      <Card
        className="customer_search_card"
        sx={{
          overflow: "visible",
          position: "relative",
        }}
      >
        <Typography variant="h5">Meter Type</Typography>
        <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
          <Typography variant="body1" fontWeight={600}>
            Meter Id:
          </Typography>

          <CustomSearch
            value={searchValue.value}
            onChange={(e) =>
              setSearchValue({ ...searchValue, value: e.target.value })
            }
            placeholder={"Search Meter.."}
            sx={{
              width: "500px",
              height: "40px",
            }}
            onClick={handleSearch}
          />

          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              height: "35px",
              color: "#ffffff",
            }}
            onClick={handleSubmit}
          />
        </Stack>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <CustomOutlinedSelect
                label={"Meter Commodity Type"}
                options={meterCommodityTypeOptions}
                placeholder={"Select"}
                name={"meterCommodityType"}
                onChange={(e) =>
                  handleMeterCommodityTypeChange(e as OptionType)
                }
                value={
                  typeof meterRegisterData.meterCommodityType === "object"
                    ? meterRegisterData.meterCommodityType
                    : undefined
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <CustomOutlinedSelect
                label={"Head End System"}
                options={headEndSystemOptions}
                placeholder={"Select"}
                name={"headEndSystem"}
                onChange={(newValue) =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    headEndSystem: newValue.value as string,
                  })
                }
                value={meterRegisterData.headEndSystem}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <CustomOutlinedSelect
                label={"Meter Register Type"}
                options={meterRegisterTypeOptions}
                placeholder={"Select"}
                name={"meterRegisterType"}
                onChange={(newValue) =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    meterRegisterType: newValue.value as string,
                  })
                }
                value={meterRegisterData.meterRegisterType}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
              <CustomOutlinedSelect
                label={"Metering Technology"}
                options={meteringTechnologyOptions}
                placeholder={"Select"}
                name={"meteringTechnology"}
                onChange={(newValue) =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    meteringTechnology: newValue.value as string,
                  })
                }
                value={meterRegisterData.meteringTechnology}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
              <CustomOutlinedSelect
                label={"Communication Interface"}
                options={communicationInterfaceOptions}
                placeholder={"Select"}
                name={"communicationInterface"}
                onChange={(newValue) =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    communicationInterface: newValue.value as string,
                  })
                }
                value={meterRegisterData.communicationInterface}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
              <DatePickerInput
                label={"Valid From"}
                format={"dd/MM/yyyy"}
                placeholder={"dd/mm/yyyy"}
                value={
                  meterRegisterData.validFrom
                    ? new Date(meterRegisterData.validFrom)
                    : null
                }
                onChange={(event) => {
                  setMeterRegisterData({
                    ...meterRegisterData,
                    validFrom: event,
                  });
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={"10px"}>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormControlLabel
                control={<Checkbox defaultChecked />}
                label={"Data Logging"}
                checked={meterRegisterData?.dataLogging}
                onChange={() =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    dataLogging: !meterRegisterData?.dataLogging,
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormControlLabel
                control={<Checkbox defaultChecked />}
                label={"Voltage and Current Inputs"}
                checked={meterRegisterData?.voltageCurrentInputs}
                onChange={() =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    voltageCurrentInputs:
                      !meterRegisterData?.voltageCurrentInputs,
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormControlLabel
                control={<Checkbox defaultChecked />}
                label={"Reverse Flow Detection"}
                checked={meterRegisterData?.reverseFlowDetection}
                onChange={() =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    reverseFlowDetection:
                      !meterRegisterData?.reverseFlowDetection,
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormControlLabel
                control={<Checkbox defaultChecked />}
                label={"Pulse Output"}
                checked={meterRegisterData?.pulseOutput}
                onChange={() =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    pulseOutput: !meterRegisterData?.pulseOutput,
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormControlLabel
                control={<Checkbox defaultChecked />}
                label={"Temperature and Humidity Sensors"}
                checked={meterRegisterData?.temperatureHumiditySensors}
                onChange={() =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    temperatureHumiditySensors:
                      !meterRegisterData?.temperatureHumiditySensors,
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <FormControlLabel
                control={<Checkbox defaultChecked />}
                label={"Data Ports"}
                checked={meterRegisterData?.dataPorts}
                onChange={() =>
                  setMeterRegisterData({
                    ...meterRegisterData,
                    dataPorts: !meterRegisterData?.dataPorts,
                  })
                }
              />
            </Grid>
          </Grid>

          {/* Type-Specific Fields Section */}
          {meterRegisterData.meterCommodityType &&
            dynamicTypeSpecificFields.length > 0 && (
              <>
                <Grid container spacing={2} mt={"30px"}>
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#1976d2", fontWeight: 600 }}
                    >
                      {typeof meterRegisterData.meterCommodityType === "object"
                        ? meterRegisterData.meterCommodityType.label
                        : meterRegisterData.meterCommodityType}{" "}
                      Specific Fields
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", marginBottom: "10px" }}
                    >
                      These fields are specific to{" "}
                      {typeof meterRegisterData.meterCommodityType === "object"
                        ? meterRegisterData.meterCommodityType.label
                        : meterRegisterData.meterCommodityType}{" "}
                      meter types
                    </Typography>
                  </Grid>

                  {/* Render type-specific fields */}
                  {dynamicTypeSpecificFields.map((field) => (
                    <Grid
                      key={field.name}
                      size={{ xs: 12, md: 4, lg: 4 }}
                      mt={"10px"}
                    >
                      {renderTypeSpecificField(field)}
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

          <Grid container spacing={2} mt={"10px"}></Grid>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default MeterType;
